import { useState, useRef, useEffect } from 'react';

interface Client {
  id: number | string;
  nom: string;
  telephone?: string;
}

interface ClientAutocompleteProps {
  clients: Client[];
  value: string;
  onChange: (nom: string, id: string | null) => void;
  placeholder?: string;
  required?: boolean;
}

export default function ClientAutocomplete({ clients, value, onChange, placeholder = "Rechercher ou ajouter un client...", required = false }: ClientAutocompleteProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);

  const filtered = clients.filter(c => 
    c.nom.toLowerCase().includes(value.toLowerCase()) || 
    (c.telephone && c.telephone.includes(value))
  ).slice(0, 5);

  const showCreateOption = value.length > 1 && !clients.some(c => c.nom.toLowerCase() === value.toLowerCase());

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (c: Client) => {
    onChange(c.nom, String(c.id));
    setIsOpen(false);
  };

  const handleCreateNew = () => {
    onChange(value, null);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={containerRef}>
      <div className="relative">
        <i className="ri-user-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={value}
          onChange={(e) => {
             onChange(e.target.value, null);
             setIsOpen(true);
             setHighlightedIndex(-1);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          required={required}
          className={`w-full pl-9 pr-4 py-2 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-terre-400 font-bold ${required && !value ? 'border-red-200 bg-red-50/30' : 'border-gray-200 bg-white'}`}
        />
        {value && (
           <button 
             onClick={() => onChange('', null)}
             className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
             title="Effacer la recherche"
           >
             <i className="ri-close-circle-fill" />
           </button>
        )}
      </div>

      {isOpen && (value.length > 0 || filtered.length > 0) && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-100 rounded-xl shadow-xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="max-h-60 overflow-y-auto">
            {filtered.length > 0 ? (
              <div className="p-1">
                <p className="px-3 py-1.5 text-[10px] font-black text-gray-400 uppercase tracking-widest">Clients existants</p>
                {filtered.map((c, index) => (
                  <button
                    key={c.id}
                    onClick={() => handleSelect(c)}
                    className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-terre-50 transition-colors ${highlightedIndex === index ? 'bg-terre-50' : ''}`}
                  >
                    <div>
                      <span className="font-bold text-gray-900">{c.nom}</span>
                      {c.telephone && <span className="text-xs text-gray-400 ml-2">({c.telephone})</span>}
                    </div>
                    <i className="ri-arrow-right-s-line text-gray-300" />
                  </button>
                ))}
              </div>
            ) : !showCreateOption && (
              <div className="p-4 text-center text-xs text-gray-400">
                Aucun client trouvé
              </div>
            )}

            {showCreateOption && (
              <div className="border-t border-gray-50 p-1">
                <button
                  onClick={handleCreateNew}
                  className="w-full text-left px-3 py-3 text-sm flex items-center gap-3 hover:bg-gold-50 text-gold-700 transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-gold-100 flex items-center justify-center text-gold-600">
                    <i className="ri-user-add-line" />
                  </div>
                  <div>
                    <p className="font-black leading-tight">Ajouter "{value}"</p>
                    <p className="text-[10px] opacity-70 font-bold uppercase">Nouveau client passager</p>
                  </div>
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
